import { useState, useMemo, useEffect } from 'react';
import { ChevronLeft, ChevronRight, RotateCcw } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useRouterState } from '@tanstack/react-router';
import { useSession } from '../../contexts/SessionContext';
import { 
  getEmployeeYearOverview, 
  getEmployeeMonthlyOverview, 
  getEmployeeProjectOverview,
  getEmployeeDetailedTimeEntries 
} from '../../services/timeEntryService';
import { YTDProjectsTable } from './YTDProjectsTable';
import { MonthlyComparisonTable } from './MonthlyComparisonTable';
import { InternalProjectTable } from './InternalProjectTable';
import { DetailedTimeEntriesTable } from './DetailedTimeEntriesTable';
import { OverviewKPI } from './OverviewKPI';
import './overview.css';

const monthNames = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
const monthNamesShort = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

export function TimeTrackingDashboard() {
  const { employeeId } = useSession();
  const router = useRouterState();
  const currentDate = new Date();
  const nowYear = currentDate.getFullYear();
  const nowMonth = currentDate.getMonth();
  const [selectedYear, setSelectedYear] = useState(nowYear);
  const [selectedMonthForFilter, setSelectedMonthForFilter] = useState<number | null>(null);
  const [selectedProjectForFilter, setSelectedProjectForFilter] = useState<string | null>(null);

  const { data: yearOverview, isLoading: isLoadingYear, error: yearError, refetch: refetchYear } = useQuery({
    queryKey: ['year-overview', selectedYear, employeeId],
    queryFn: () => getEmployeeYearOverview(selectedYear, employeeId),
    enabled: !!employeeId,
    staleTime: 1000 * 60 * 5,
  });

  const { data: monthlyOverview, isLoading: isLoadingMonthly, error: monthlyError, refetch: refetchMonthly } = useQuery({
    queryKey: ['monthly-overview', selectedYear, employeeId],
    queryFn: () => getEmployeeMonthlyOverview(selectedYear, employeeId),
    enabled: !!employeeId,
    staleTime: 1000 * 60 * 5,
  });

  const { data: projectOverview, isLoading: isLoadingProject, error: projectError, refetch: refetchProject } = useQuery({
    queryKey: ['project-overview', selectedYear, employeeId, selectedMonthForFilter],
    queryFn: () => getEmployeeProjectOverview(selectedYear, employeeId, selectedMonthForFilter ?? null),
    enabled: !!employeeId,
    staleTime: 1000 * 60 * 5,
  });

  const { data: detailedEntries, refetch: refetchDetailed } = useQuery({
    queryKey: ['overview-detailed-entries', selectedYear, employeeId, selectedMonthForFilter, selectedProjectForFilter],
    queryFn: () => getEmployeeDetailedTimeEntries(selectedYear, employeeId, selectedMonthForFilter ?? undefined, selectedProjectForFilter ?? undefined),
    enabled: !!employeeId,
    staleTime: 1000 * 60 * 5,
  });

  useEffect(() => {
    if (employeeId && router.location.pathname === '/overview') {
      refetchYear();
      refetchMonthly();
      refetchProject();
      refetchDetailed();
    }
  }, [employeeId, router.location.pathname, refetchYear, refetchMonthly, refetchProject, refetchDetailed]);

  const monthlyData = useMemo(() => {
    if (!monthlyOverview) return [];
    const dataMap = new Map(monthlyOverview.map(m => [m.month, m]));
    return monthNamesShort.map((monat, index) => {
      const month = dataMap.get(index + 1);
      if (!month) {
        return {
          monat,
          soll: 0,
          ist: 0,
          billable: 0,
          nonBillable: 0,
        };
      }
      return {
        monat,
        soll: month.totalplannedhours,
        ist: month.totalprojecthours,
        billable: month.totalbillablehours,
        nonBillable: month.totalprojecthours - month.totalbillablehours,
      };
    });
  }, [monthlyOverview]);


  const externalProjects = useMemo(() => {
    if (!projectOverview) return [];
    return projectOverview.externalProject.map(project => ({
      projektnummer: project.projectnumber,
      name: project.projectname,
      billableHours: project.totalbillablehours,
      nonBillableHours: project.totalnonbillablehours,
      totalHours: project.totalbillablehours + project.totalnonbillablehours,
      billableRate: (project.totalbillablehours + project.totalnonbillablehours) > 0 
        ? (project.totalbillablehours / (project.totalbillablehours + project.totalnonbillablehours)) * 100 
        : 0,
    }));
  }, [projectOverview]);

  const internalProject = useMemo(() => {
    if (!projectOverview || !projectOverview.internalProject) return null;
    const internal = projectOverview.internalProject;
    if (internal.internalhours === 0 && internal.absencehours === 0) return null;
    return {
      projektnummer: '000',
      name: 'Interne Verwaltung & Absenzen',
      internalHours: internal.internalhours,
      absenceHours: internal.absencehours,
      totalHours: internal.internalhours + internal.absencehours,
    };
  }, [projectOverview]);

  const ytdEndMonthInclusive = useMemo(() => {
    if (selectedYear < nowYear) return 11;
    if (selectedYear > nowYear) return 11;
    return nowMonth;
  }, [selectedYear, nowYear, nowMonth]);

  const ytdData = useMemo(() => {
    return monthlyData.slice(0, ytdEndMonthInclusive + 1);
  }, [monthlyData, ytdEndMonthInclusive]);

  const ytdSoll = ytdData.reduce((sum, m) => sum + m.soll, 0);
  const ytdBillable = ytdData.reduce((sum, m) => sum + m.billable, 0);
  const ytdNonBillable = ytdData.reduce((sum, m) => sum + m.nonBillable, 0);
  const ytdProjektstunden = ytdBillable + ytdNonBillable;

  const highlightMonthIndex = selectedYear === nowYear ? nowMonth : -1;

  const billableRate = ytdProjektstunden > 0 ? (ytdBillable / ytdProjektstunden) * 100 : 0;

  const isLoading = isLoadingYear || isLoadingMonthly || isLoadingProject;
  const error = yearError || monthlyError || projectError;

  const handleMonthClick = (monthIndex: number) => {
    if (selectedMonthForFilter === monthIndex) {
      setSelectedMonthForFilter(null);
    } else {
      setSelectedMonthForFilter(monthIndex);
    }
  };

  const handleProjectSelect = (projectNumber: string | null) => {
    setSelectedProjectForFilter(projectNumber);
  };

  const handleResetFilters = () => {
    setSelectedMonthForFilter(null);
    setSelectedProjectForFilter(null);
  };

  const hasActiveFilters = selectedMonthForFilter !== null || selectedProjectForFilter !== null;

  if (isLoading) {
    return (
      <div className="overview-container ov-min-h-screen ov-bg-gray-50 ov-p-6 ov-flex ov-items-center ov-justify-center">
        <div className="ov-text-gray-600">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="overview-container ov-min-h-screen ov-bg-gray-50 ov-p-6 ov-flex ov-items-center ov-justify-center">
        <div className="ov-text-red-600">Error loading data: {error instanceof Error ? error.message : 'Unknown error'}</div>
      </div>
    );
  }

  return (
    <div className="overview-container ov-min-h-screen ov-bg-gray-50 ov-p-6">
      <div className="ov-max-w-7xl ov-mx-auto ov-space-y-6">

        <div className="ov-flex ov-items-center ov-justify-end ov-gap-3 ov-rounded-lg ov-p-4">
          <div className="ov-flex ov-items-center ov-gap-2 ov-bg-white ov-rounded-lg ov-p-1 ov-border ov-border-gray-200">
            <button
              onClick={() => setSelectedYear(selectedYear - 1)}
              disabled={selectedYear <= currentDate.getFullYear() - 1}
              className="ov-p-2 ov-bg-transparent ov-border-none ov-outline-none disabled:ov-opacity-50 disabled:ov-cursor-not-allowed ov-cursor-pointer"
            >
              <ChevronLeft className="ov-w-4 ov-h-4" />
            </button>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              className="ov-px-3 ov-py-2 ov-text-sm ov-bg-transparent ov-border-none ov-outline-none ov-cursor-pointer"
              style={{ background: 'transparent', border: 'none', outline: 'none' }}
            >
              {[currentDate.getFullYear()-1, currentDate.getFullYear(), currentDate.getFullYear()+1].map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
            <button
              onClick={() => setSelectedYear(selectedYear + 1)}
              disabled={selectedYear >= currentDate.getFullYear() + 1}
              className="ov-p-2 ov-bg-transparent ov-border-none ov-outline-none disabled:ov-opacity-50 disabled:ov-cursor-not-allowed ov-cursor-pointer"
            >
              <ChevronRight className="ov-w-4 ov-h-4" />
            </button>
          </div>

          <button
            onClick={handleResetFilters}
            className={`ov-flex ov-items-center ov-gap-2 ov-px-4 ov-py-2 ov-rounded-lg ov-border ov-transition-colors ${hasActiveFilters
              ? 'ov-bg-green-600 ov-text-white ov-border-green-600 ov-hover:bg-green-700'
              : 'ov-bg-white ov-text-gray-700 ov-border-gray-200 ov-hover:bg-gray-50'
              }`}
          >
            <RotateCcw className="ov-w-4 ov-h-4" />
            <span className="ov-text-sm">Filter zurücksetzen</span>
          </button>
        </div>

        <OverviewKPI
          yearOverview={yearOverview}
          ytdProjektstunden={ytdProjektstunden}
          ytdSoll={ytdSoll}
          ytdBillable={ytdBillable}
          billableRate={billableRate}
          selectedYear={selectedYear}
        />

        <div className="ov-grid ov-grid-cols-1 ov-md:grid-cols-45 ov-gap-6">
          <div className="ov-bg-white ov-rounded-lg ov-border ov-border-gray-200 ov-shadow-sm ov-overflow-hidden">
            <MonthlyComparisonTable
              data={monthlyData}
              highlightMonthIndex={highlightMonthIndex}
              totalsEndMonthInclusive={ytdEndMonthInclusive}
              year={selectedYear}
              selectedMonthForFilter={selectedMonthForFilter}
              onMonthClick={handleMonthClick}
            />
          </div>

          <div className="ov-flex ov-flex-col ov-gap-6">
            <div className="ov-bg-white ov-rounded-lg ov-border ov-border-gray-200 ov-shadow-sm ov-overflow-hidden">
              <InternalProjectTable
                project={internalProject}
                year={selectedYear}
                monthName={selectedMonthForFilter !== null ? monthNames[selectedMonthForFilter] : monthNames[ytdEndMonthInclusive]}
                isMonthFiltered={selectedMonthForFilter !== null}
                onProjectSelect={handleProjectSelect}
                selectedProjectNumber={selectedProjectForFilter}
              />
            </div>

            <div className="ov-bg-white ov-rounded-lg ov-border ov-border-gray-200 ov-shadow-sm ov-overflow-hidden">
              <YTDProjectsTable
                projects={externalProjects}
                year={selectedYear}
                monthName={selectedMonthForFilter !== null ? monthNames[selectedMonthForFilter] : monthNames[ytdEndMonthInclusive]}
                title="Kundenprojekte"
                isMonthFiltered={selectedMonthForFilter !== null}
                onProjectSelect={handleProjectSelect}
                selectedProjectNumber={selectedProjectForFilter}
                selectedMonthForFilter={selectedMonthForFilter}
              />
            </div>
          </div>
        </div>


        <div className="ov-w-full ov-bg-white ov-rounded-lg ov-border ov-border-gray-200 ov-shadow-sm ov-overflow-hidden">
          <DetailedTimeEntriesTable
            entries={detailedEntries || []}
            selectedMonth={selectedMonthForFilter}
            selectedProject={selectedProjectForFilter}
            year={selectedYear}
          />
        </div>
      </div>
    </div>
  );
}

